import { forwardRef, useState, type MouseEvent } from "react";

import {
  getFastlyOptimizedProductImageUrl,
  getProductImageAltText,
  getProductImageUrl,
  ProductImageSize,
} from "../../../helpers/product-image-helpers";
import RtrImage from "./rtr-image";

import type ProductImageGroup from "@rtr/godmother/dist/types/ProductImageGroup";

export interface ProductImageProps {
  altText?: string;
  className?: string;
  imageGroup?: ProductImageGroup | null; // null allowed here because `getProductImageUrlsByOrientation()` can return null
  imageSize: ProductImageSize;
  fallbackSrc?: string;
  onClick?: (event: MouseEvent<HTMLImageElement>) => void;
  product?: {
    displayName?: string;
    designer?: {
      displayName?: string;
    };
  };
  sizes?: string;
  srcSetSizes?: Array<{
    xSize: number;
    path?: string;
  }>;
  style?: React.CSSProperties;
  loading?: "lazy" | "eager";
  fetchPriority?: string;
  draggable?: boolean;
}

const ProductImage = forwardRef<HTMLImageElement, ProductImageProps>(
  (
    {
      altText,
      className,
      imageGroup,
      imageSize,
      fallbackSrc,
      onClick,
      product,
      sizes,
      srcSetSizes,
      style,
      loading = "eager",
      fetchPriority,
      draggable = true,
    },
    ref
  ) => {
    const [hasErrorLoading, setErrorLoading] = useState(false);
    const alt = altText || getProductImageAltText(product?.displayName, product?.designer?.displayName);

    if (!imageGroup && !fallbackSrc) return null;

    const x1080ImagePath = imageGroup?.[ProductImageSize.x1080.path as keyof ProductImageGroup];
    const queryOptimizedImage = Boolean(x1080ImagePath);

    const fastlyOptimizedImageHref = getFastlyOptimizedProductImageUrl({
      src: x1080ImagePath || fallbackSrc || "",
      width: imageSize?.xSize,
    })?.href;

    const responsiveFastlyOptimizedImageHrefs = (): string | undefined => {
      if (!Array.isArray(srcSetSizes) || srcSetSizes.length === 0) return;

      return srcSetSizes
        .map(
          size =>
            `${
              getFastlyOptimizedProductImageUrl({
                src: x1080ImagePath || fallbackSrc || "",
                width: size?.xSize,
              })?.href
            } ${size?.xSize}w`
        )
        .join(", ");
    };

    const fastlyUnoptimizedImageHref = getProductImageUrl(
      imageGroup?.[imageSize.path as keyof ProductImageGroup] || fallbackSrc || ""
    )?.href;

    const responsiveFastlyUnoptimizedImageHrefs = (): string | undefined => {
      if (!Array.isArray(srcSetSizes) || srcSetSizes.length === 0) return;

      return srcSetSizes
        .map(size => {
          let url: URL;
          if (size.path && imageGroup?.[size.path as keyof ProductImageGroup]) {
            url = getProductImageUrl(imageGroup?.[size.path as keyof ProductImageGroup]);
          } else if (fallbackSrc) {
            url = getProductImageUrl(fallbackSrc);
          } else {
            url = getProductImageUrl("");
          }

          return `${url.href} ${size?.xSize}w`;
        })
        .join(", ");
    };

    if (queryOptimizedImage) {
      return (
        <RtrImage
          alt={alt}
          className={className}
          itemProp={"image"}
          onClick={onClick}
          onError={() => setErrorLoading(true)}
          ref={ref}
          src={hasErrorLoading ? fastlyUnoptimizedImageHref : fastlyOptimizedImageHref}
          srcSet={hasErrorLoading ? responsiveFastlyUnoptimizedImageHrefs() : responsiveFastlyOptimizedImageHrefs()}
          sizes={sizes}
          style={style}
          loading={loading}
          fetchpriority={fetchPriority}
          draggable={draggable}
        />
      );
    }

    return (
      <RtrImage
        alt={alt}
        className={className}
        itemProp={"image"}
        onClick={onClick}
        ref={ref}
        src={fastlyUnoptimizedImageHref}
        style={style}
        loading={loading}
        fetchpriority={fetchPriority}
        draggable={draggable}
      />
    );
  }
);

ProductImage.displayName = "ProductImage";

export default ProductImage;
